"use client";

import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import { Label } from "../../@/components/ui/label";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import {
    Field,
    FieldLabel,
    FieldDescription,
    FieldError,
    FieldGroup,
    FieldLegend,
    FieldSeparator,
    FieldSet,
    FieldContent,
    FieldTitle,
} from "../../@/components/ui/field";

// Validation schema
const addPathwayTypeSchema = z.object({
    name: z.string().min(1, "Title is required"),
    description: z.string().optional(),
});

export default function AddPathwayTypeDialog({ onAdded }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pathwayTypes, setPathwayTypes] = useState([]);
    const { getToken } = useAuth();

    const form = useForm({
        resolver: zodResolver(addPathwayTypeSchema),
        defaultValues: {
            name: "",
            description: "",
        },
    });

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            const token = await getToken();
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            };

            const payload = {
                name: data.name,
                description: data.description,
            }

            const response = await axios.post(api_endpoint + "/pathway/levels/create", payload, reqConfig);
            console.log("Pathway type created:", response.data);
            toast.success("Pathway type created successfully");
            setOpen(false);
            form.reset();

            if (onAdded) {
                onAdded();
            }
        } catch (error) {
            console.error("Error creating pathway type:", error);
            toast.error("Failed to create pathway type. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.reset();
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="" className="w-full md:w-auto">
                    Add Type +
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-(--neutral-900)">
                        Add Pathway Type
                    </DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">
                        Create a new pathway type with title and description.
                    </DialogDescription>
                </DialogHeader>


                <form
                    id="add-pathway-type-form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-6"
                >
                    {/* Title Field */}
                    <Field>
                        <FieldLabel required htmlFor="name">Title</FieldLabel>
                        <Input
                            id="name"
                            placeholder="Enter pathway type title"
                            disabled={loading}
                            {...form.register("name")}
                        />
                        <FieldError>
                            {form.formState.errors.name?.message}
                        </FieldError>
                    </Field>

                    {/* Description Field */}
                    <Field>
                        <FieldLabel htmlFor="description">Description</FieldLabel>
                        <Textarea
                            id="description"
                            placeholder="Enter pathway type description"
                            rows={4}
                            className="resize-none"
                            disabled={loading}
                            {...form.register("description")}
                        />
                        <FieldError>
                            {form.formState.errors.description?.message}
                        </FieldError>
                    </Field>
                </form>

                {/* Action Buttons */}
                <DialogFooter className="">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="font-body"
                    >
                        Cancel
                    </Button>
                    <Button
                        form="add-pathway-type-form"
                        type="submit"
                        variant=""
                        disabled={loading}
                        className=""
                    >
                        {loading ? <ClipLoader size={20} color="#fff" /> : "Create Type"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}